import { getData, postData, putData, patchData, deleteData } from './FetchNodeServices.js';
import type { MockQuotation } from '@/mocks/billing';

export interface QuotationPayload {
  customerId: string;
  customerName: string;
  customerGSTIN?: string;
  customerPhone?: string;
  customerEmail?: string;
  warehouseId?: string;
  date?: string;
  validUntil?: string;
  validityDays?: number;
  status?: string;
  subtotal: number;
  discountAmount: number;
  taxableAmount: number;
  cgst: number;
  sgst: number;
  igst: number;
  taxAmount: number;
  totalAmount: number;
  notes?: string;
  termsAndConditions?: string;
  paymentTerms?: string;
  deliveryTerms?: string;
  preparedBy?: string;
  items: Array<{
    itemId?: string;
    itemName: string;
    hsnCode?: string;
    description?: string;
    qty: number;
    unit?: string;
    rate: number;
    discountPercent?: number;
    discountAmount?: number;
    taxableAmount?: number;
    taxRate?: number;
    cgst?: number;
    sgst?: number;
    igst?: number;
    totalAmount?: number;
  }>;
}

export const quotationService = {
  listQuotations: async (params?: {
    search?: string;
    status?: string;
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<MockQuotation[]> => {
    try {
      const res = await getData('api/v1/quotations', params || {});
      if (res && res.success && Array.isArray(res.data)) {
        return res.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching quotations:', error);
      throw error;
    }
  },

  getQuotation: async (id: string): Promise<MockQuotation> => {
    const res = await getData(`api/v1/quotations/${id}`);
    if (res && res.success) {
      return res.data;
    }
    throw new Error(res?.message || 'Failed to fetch quotation');
  },

  createQuotation: async (payload: QuotationPayload): Promise<MockQuotation> => {
    const res = await postData('api/v1/quotations', payload);
    if (!res || res.success === false) {
      throw new Error(res?.message || 'Failed to create quotation');
    }
    return res.data;
  },

  updateQuotation: async (id: string, payload: QuotationPayload): Promise<MockQuotation> => {
    const res = await putData(`api/v1/quotations/${id}`, payload);
    if (!res || res.success === false) {
      throw new Error(res?.message || 'Failed to update quotation');
    }
    return res.data;
  },

  updateStatus: async (
    id: string,
    status: string,
    extra?: { convertedToSOId?: string; convertedToSONumber?: string }
  ): Promise<any> => {
    const res = await patchData(`api/v1/quotations/${id}/status`, {
      status,
      ...extra,
    });
    if (!res || res.success === false) {
      throw new Error(res?.message || 'Failed to update quotation status');
    }
    return res.data;
  },

  deleteQuotation: async (id: string): Promise<void> => {
    const res = await deleteData(`api/v1/quotations/${id}`);
    if (!res || res.success === false) {
      throw new Error(res?.message || 'Failed to delete quotation');
    }
  },
};
